import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Form } from "antd";
import { useIntl } from "react-intl";
import moment from "moment";

import RangePickerItem, {
  getDateFromRangePicker,
} from "../../app/generic/rangePickerItem";
import Load from "../../app/generic/load";
import InputItem from "../../app/generic/inputItem";
import SelectItem from "../../app/generic/selectItem";
import TableFilter from "../../app/generic/tableFilter";
import CheckboxItem from "../../app/generic/checkboxItem";
import SelectItemWithLoad from "../../app/generic/selectItemWithLoad";
import FilterSubmit from "../../app/generic/tableFilter/filterSubmit";
import NumberWithCurrency from "../../app/generic/numberWithCurrency";
import { loadCurrencies, loadWrappings } from "../../../modules/staticDataLoad";
import { defaultMaxCodAmoutInCzk } from "../../consignment/servicesForm/cashOnDelivery";

const dateTimeFrom = moment(moment.now()).add(-7, "days");
const dateTimeFromSent = moment(moment.now());
const dateTimeFromTrack = moment(moment.now()).add(-5, "days");

const getFormName = (sentList, trackingList) =>
  `consignment-list-${trackingList ? "track" : sentList ? "sent" : "work"}`;

function ConsignmentListFilter({
  form,
  sentList,
  showFilter,
  trackingList,
  onSetShowFilter,
  setFilterParams,
  loadConsignments,
}) {
  const intl = useIntl();
  const formItemLayout = {
    labelCol: { span: 7 },
    wrapperCol: { span: 15 },
    colon: false,
  };
  const { getFieldDecorator } = form;

  const [formName, setFormName] = useState(getFormName(sentList, trackingList));
  useEffect(
    () => setFormName(getFormName(sentList, trackingList)),
    [sentList, trackingList]
  );

  const [filterState] = useState(
    useSelector((state) => state.filter[formName])
  );
  useEffect(
    () => filterState && onSetShowFilter(true),
    [filterState, onSetShowFilter]
  );

  useEffect(() => {
    loadConsignments && loadConsignments(filterState && filterState.data);
  }, [loadConsignments, filterState]);

  const [currencies, setCurrencies] = useState();
  useEffect(() => loadCurrencies(setCurrencies), []);

  const [wrappings, setWrappings] = useState();
  useEffect(() => loadWrappings(setWrappings), []);

  const handleFiltering = useCallback(
    (e, onSetFilterState) => {
      e.preventDefault();
      form.validateFields((err, values) => {
        if (!err) {
          const params = {
            ...values,
            createFrom: getDateFromRangePicker(values.createDate, 0),
            createTo: getDateFromRangePicker(values.createDate, 1),
            sentFrom: getDateFromRangePicker(values.sentDate, 0),
            sentTo: getDateFromRangePicker(values.sentDate, 1),
            createDate: undefined,
            sentDate: undefined,
          };
          setFilterParams(params);
          loadConsignments(params);
          onSetFilterState(params);
        }
      });
    },
    [form, setFilterParams, loadConsignments]
  );

  return (
    <TableFilter showFilter={showFilter}>
      <Form {...formItemLayout}>
        <Row>
          <Col span={12}>
            <InputItem
              label={intl.formatMessage({
                id: "consignment.pickup",
                defaultMessage: "Nakládka",
              })}
              fieldName="pickup"
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
            />
            <InputItem
              label={intl.formatMessage({
                id: "consignment.customerReference",
                defaultMessage: "Reference",
              })}
              fieldName="customerReference"
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
            />
            {/* <InputItem
              label="Tvůrce zásilky"
              fieldName="creator"
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
            /> */}
            {sentList && (
              <RangePickerItem
                showRanges
                label={intl.formatMessage({
                  id: "consignment.createDate",
                  defaultMessage: "Vznik zásilky",
                })}
                fieldName="createDate"
                initialDateFrom={!sentList && dateTimeFrom}
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
              />
            )}
            <InputItem
              variableSymbol
              label={intl.formatMessage({
                id: "consignment.variableSymbol",
                defaultMessage: "Variabilní symbol dobírky",
              })}
              fieldName="codVariableSymbol"
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
            />
            <SelectItemWithLoad skeleton options={wrappings}>
              <SelectItem
                allowClear
                options={wrappings}
                fieldName="wrappingId"
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
                label={intl.formatMessage({
                  id: "consignment.type",
                  defaultMessage: "Typ",
                })}
              />
            </SelectItemWithLoad>
          </Col>
          <Col span={12}>
            <InputItem
              label={intl.formatMessage({
                id: "consignment.delivery",
                defaultMessage: "Vykládka",
              })}
              fieldName="delivery"
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
            />
            {sentList ? (
              <InputItem
                label={intl.formatMessage({
                  id: "consignment.code",
                  defaultMessage: "Číslo zásilky",
                })}
                fieldName="code"
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
              />
            ) : (
              <RangePickerItem
                showRanges
                label={intl.formatMessage({
                  id: "consignment.createDate",
                  defaultMessage: "Vznik zásilky",
                })}
                fieldName="createDate"
                initialDateFrom={dateTimeFrom}
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
              />
            )}
            {/* {sentList && (
              <InputItem
                label={intl.formatMessage({
                  id: "consignment.createDate",
                  defaultMessage: "Objednávatel zásilky"})}
                fieldName="sender"
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
              />
            )} */}
            {sentList && (
              <RangePickerItem
                showRanges
                label={intl.formatMessage({
                  id: "consignment.sentDate",
                  defaultMessage: "Odeslání zásilky",
                })}
                fieldName="sentDate"
                initialDateFrom={
                  trackingList
                    ? dateTimeFromTrack
                    : sentList
                    ? dateTimeFromSent
                    : dateTimeFrom
                }
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
              />
            )}
            {currencies ? (
              <NumberWithCurrency
                amountOffset={2}
                label={intl.formatMessage({
                  id: "consignment.codAmount",
                  defaultMessage: "Hodnota dobírky",
                })}
                currencies={currencies}
                amountFieldName="codAmount"
                currencyFieldName="codCurrencyId"
                formItemLayout={formItemLayout}
                maxAmount={defaultMaxCodAmoutInCzk}
                getFieldDecorator={getFieldDecorator}
                initialCurrency={currencies && currencies[0].id}
              />
            ) : (
              <Load />
            )}
            {sentList && (
              <CheckboxItem
                fieldName="notPrintedLabel"
                label={intl.formatMessage({
                  id: "consignment.notPrintedLabel",
                  defaultMessage: "Jen zásilky bez vytištěného štítku",
                })}
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
              />
            )}
            <FilterSubmit
              form={form}
              formName={formName}
              filterState={filterState}
              onFilterClick={handleFiltering}
            />
          </Col>
        </Row>
      </Form>
    </TableFilter>
  );
}

export default Form.create()(ConsignmentListFilter);
